import {
  extractRefIds,
  type EffectProperties,
  type UpdateEffectConfigAction,
} from "@skilitics/nautilus-core-designer-data";

import type { GetManifestByTypeId } from "../../../types/index.ts";
import type { NodeActionInterceptor } from "../types.ts";

interface Options {
  getManifest: GetManifestByTypeId;
}

// remove effect configs where the referenced node is removed
// TODO: we want to callback to effect builders to let them decide how to handle the effect reference
// but for now this is all we need for interlude effects
export const effectReferenceInterceptorCreate = (_: Options): NodeActionInterceptor => ({
  afterAction: (action, { getState, dispatch }) => {
    if (action.type !== "REMOVE") return;

    const nodeIdMap = new Map(Object.keys(getState().designsById).map((id) => [Number(id), true]));

    // NOTE: any effects on or targeting the node are removed by the effects reducer itself

    type EffectConfig = EffectProperties["config"];

    // any effects that reference the node have to be updated to remove the ref
    const removeEffectReferencesToMissingNodeActions = Object.entries(getState().triggerDesigns.effects.byId)
      .map(([idStr, effect]): [number, EffectConfig | null] => {
        const effectId = Number(idStr);
        const refIdsToRemove = extractRefIds(effect.config.refs).filter((nodeId) => !nodeIdMap.has(nodeId));
        if (refIdsToRemove.length === 0) return [effectId, null];
        const newConfig: EffectConfig = {
          ...effect.config,
          refs: Object.fromEntries(
            Object.entries(effect.config.refs ?? {})
              .map(([name, idOrIds]) => {
                if (Array.isArray(idOrIds)) {
                  const newIds = idOrIds.filter((id) => !id || !refIdsToRemove.includes(id));
                  return [name, newIds];
                }
                if (idOrIds && refIdsToRemove.includes(idOrIds)) {
                  return [name, undefined];
                }
                return [name, idOrIds];
              })
              .filter((v) => !!v[1]),
          ),
        };
        return [effectId, newConfig];
      })
      .filter((v): v is [number, EffectConfig] => v[1] !== null)
      .map(([id, config]): UpdateEffectConfigAction => ({ type: "UPDATE_EFFECT_CONFIG", payload: { id, config } }));

    for (const removeAction of removeEffectReferencesToMissingNodeActions) {
      dispatch(removeAction);
    }
  },
});
